import React, { useState } from 'react';
import { Row, Col, Card, Button, Badge, ListGroup, Table, Spinner, Alert } from 'react-bootstrap';
import useWebsiteStore from '../store/websiteStore';

const WebsiteSecurity = ({ website, results }) => {
    const [isScanning, setIsScanning] = useState(false);
    const { runSecurityCheck, error } = useWebsiteStore();

    const handleScan = async () => {
        setIsScanning(true);
        try {
            await runSecurityCheck(website.id);
        } catch (error) {
            console.error('Chyba při spuštění bezpečnostní kontroly:', error);
        } finally {
            setIsScanning(false);
        }
    };
    // Kontrola, zda jsou výsledky k dispozici
    if (!results || !Array.isArray(results) || results.length === 0) {
        return (
            <div className="text-center py-5">
                <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    Pro tento web zatím nejsou k dispozici bezpečnostní výsledky. Spusťte kontrolu.
                    <div className="mt-3">
                        <Button
                            variant="primary"
                            onClick={() => handleScan()}
                            disabled={isScanning}
                        >
                            {isScanning ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    Probíhá kontrola...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-shield-alt me-2"></i> Spustit bezpečnostní kontrolu
                                </>
                            )}
                        </Button>
                    </div>
                </Alert>
            </div>
        );
    }

    // Použití nejnovějšího výsledku
    const latestResult = results[0];

    // Kategorizace problémů podle závažnosti
    const criticalIssues = latestResult.details.issues.filter(issue => issue.severity === 'critical' || issue.severity === 'high');
    const mediumIssues = latestResult.details.issues.filter(issue => issue.severity === 'medium');
    const lowIssues = latestResult.details.issues.filter(issue => issue.severity === 'low');


    // Pomocná funkce pro určení varianty badgí podle závažnosti
    const getSeverityVariant = (severity) => {
        switch (severity) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            case 'low': return 'info';
            default: return 'secondary';
        }
    };

    // Pomocná funkce pro překlad závažnosti
    const getSeverityText = (severity) => {
        switch (severity) {
            case 'high': return 'Kritická';
            case 'medium': return 'Střední';
            case 'low': return 'Nízká';
            default: return 'Neznámá';
        }
    };

    // Výpočet skóre na základě nalezených problémů
    const calculateScore = () => {
        const totalIssues = latestResult.details.issues.length;
        const highIssuesCount = criticalIssues.length;
        const mediumIssuesCount = mediumIssues.length;

        if (totalIssues === 0) return 100;

        // Skóre se snižuje více při závažnějších problémech
        const deduction = (highIssuesCount * 20) + (mediumIssuesCount * 10) + (lowIssues.length * 5);
        return Math.max(0, 100 - deduction);
    };

    const securityScore = calculateScore();

    // Kontrola SSL certifikátu
    const sslCheck = latestResult.details.ssl === true;

    // Kontrola verze WordPressu
    const wpVersionStatus = latestResult.details?.wordpress_version?.outdated ? 'outdated' : 'upToDate';

    return (
        <>
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3>Bezpečnostní skóre</h3>
                    <Badge
                        bg={securityScore > 80 ? 'success' : securityScore > 50 ? 'warning' : 'danger'}
                        className="fs-5 py-2 px-3"
                    >
                        {securityScore}/100
                    </Badge>
                </div>

                <Button
                    variant="primary"
                    onClick={()=>handleScan()}
                    disabled={isScanning}
                >
                    {isScanning ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Probíhá skenování...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-shield-alt me-2"></i> Spustit bezpečnostní kontrolu
                        </>
                    )}
                </Button>
            </div>

            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <p>Poslední kontrola: <strong>{new Date(latestResult.details.scan_time).toLocaleString()}</strong></p>
                    <div className="d-flex gap-4 mt-3">
                        <div>
                            <Badge bg="danger" className="p-2 d-flex align-items-center">
                                <i className="fas fa-exclamation-circle me-2"></i> {criticalIssues.length} kritických
                            </Badge>
                        </div>
                        <div>
                            <Badge bg="warning" text="dark" className="p-2 d-flex align-items-center">
                                <i className="fas fa-exclamation-triangle me-2"></i> {mediumIssues.length} středních
                            </Badge>
                        </div>
                        <div>
                            <Badge bg="info" className="p-2 d-flex align-items-center">
                                <i className="fas fa-info-circle me-2"></i> {lowIssues.length} drobných
                            </Badge>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Nalezené zranitelnosti</h5>
                </Card.Header>
                <Card.Body>
                    {latestResult.details.issues.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fas fa-shield-alt fa-4x text-success mb-3"></i>
                            <h5>Nebyly nalezeny žádné zranitelnosti!</h5>
                            <p className="text-muted">Váš web je momentálně bezpečný.</p>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {latestResult.details.issues.map((issue, index) => (
                                <ListGroup.Item key={index} className="py-4">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h5 className="mb-0">{issue.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h5>
                                        <Badge bg={getSeverityVariant(issue.severity)}>
                                            {getSeverityText(issue.severity)}
                                        </Badge>
                                    </div>
                                    <p>{issue.message}</p>
                                    <div>
                                        <h6 className="text-success">Doporučené řešení:</h6>
                                        <p className="mb-0">
                                            {issue.type === 'missing_security_headers' && 'Přidejte chybějící bezpečnostní HTTP hlavičky ve vašem webovém serveru nebo pomocí .htaccess souboru.'}
                                            {issue.type === 'exposed_files' && 'Omezte přístup k citlivým souborům pomocí .htaccess nebo je přesuňte mimo veřejně dostupný adresář.'}
                                            {issue.type === 'wordpress_outdated' && 'Aktualizujte WordPress na nejnovější verzi.'}
                                            {!['missing_security_headers', 'exposed_files', 'wordpress_outdated'].includes(issue.type) && 'Kontaktujte správce vašeho webu pro řešení tohoto problému.'}
                                        </p>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Bezpečnostní kontroly</h5>
                </Card.Header>
                <Card.Body>
                    <Table responsive>
                        <thead>
                        <tr>
                            <th>Kontrola</th>
                            <th>Stav</th>
                            <th>Detaily</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>SSL/TLS Certifikát</td>
                            <td>
                                <Badge bg={sslCheck ? 'success' : 'danger'}>
                                    {sslCheck ? 'V pořádku' : 'Problém'}
                                </Badge>
                            </td>
                            <td>
                                {sslCheck
                                    ? `HTTPS připojení je k dispozici`
                                    : 'SSL certifikát chybí nebo není platný'}
                            </td>
                        </tr>
                        <tr>
                            <td>WordPress verze</td>
                            <td>
                                <Badge bg={wpVersionStatus === 'upToDate' ? 'success' : 'warning'} text={wpVersionStatus === 'upToDate' ? '' : 'dark'}>
                                    {wpVersionStatus === 'upToDate' ? 'Aktuální' : 'Zastaralá'}
                                </Badge>
                            </td>
                            <td>
                                {latestResult.details?.wordpress_version?.version || 'Verze nedetekována'}
                                {wpVersionStatus !== 'upToDate' && ' (doporučena aktualizace)'}
                            </td>
                        </tr>
                        <tr>
                            <td>Bezpečnostní hlavičky</td>
                            <td>
                                <Badge bg={latestResult.details?.security_headers?.missing?.length === 0 ? 'success' : 'warning'}>
                                    {latestResult.details.security_headers?.missing?.length === 0 ? 'V pořádku' : 'Chybí'}
                                </Badge>
                            </td>
                            <td>
                                {latestResult.details?.security_headers?.missing.length === 0
                                    ? 'Všechny bezpečnostní hlavičky jsou nastaveny'
                                    : `Chybí: ${latestResult.details.security_headers?.missing?.join(', ')}`}
                            </td>
                        </tr>
                        <tr>
                            <td>Citlivé soubory</td>
                            <td>
                                <Badge bg={latestResult.details?.exposed_files?.length === 0 ? 'success' : 'danger'}>
                                    {latestResult.details?.exposed_files?.length === 0 ? 'Zabezpečeno' : 'Odhaleno'}
                                </Badge>
                            </td>
                            <td>
                                {latestResult.details.exposed_files?.length === 0
                                    ? 'Žádné citlivé soubory nejsou veřejně dostupné'
                                    : `Odhalené soubory: ${latestResult.details?.exposed_files?.join(', ')}`}
                            </td>
                        </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    );
};

export default WebsiteSecurity;